import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';

interface ColumnDef { id: string; title: string; }
interface RowDef    { id: string; title: string; answer_id?: string | null; }

@Component({
  selector: 'app-grid-question-table',
  templateUrl: './grid-question-table.component.html',
  styleUrls: ['./grid-question-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridQuestionTableComponent {
  private _columns: ColumnDef[] = [];
  private _rows: RowDef[] = [];

  @Output() columnsChange = new EventEmitter<ColumnDef[]>();
  @Output() rowsChange = new EventEmitter<RowDef[]>();

  /** Flag per mostrare la fase di risposta */
  @Input() isResponse = false;
  @Input() editable = true;

  /** Dati per la view di configurazione */
  displayColumns: ColumnDef[] = [];
  displayRows:    RowDef[]    = [];

  /** Definizione colonne per mat-table in risposta */
  displayedColumns: string[] = [];

  @Input()
  set columns(cols: ColumnDef[] | null) {
    const source = cols || [];
    // copia e forziamo id come string
    this._columns = source.map(c => ({ id: String(c.id), title: c.title }));
    // almeno 2 colonne
    while (this._columns.length < 2) {
      const idx = this._columns.length + 1;
      this._columns.push({ id: `col${idx}`, title: `Colonna ${idx}` });
    }
    this.displayColumns = [...this._columns];
    this.updateTableColumns();
  }
  get columns(): ColumnDef[] {
    return [...this._columns];
  }

  @Input()
  set rows(rws: RowDef[] | null) {
    const source = rws || [];
    this._rows = source.map(r => ({ id: String(r.id), title: r.title, answer_id: r.answer_id ? String(r.answer_id) : null }));
    while (this._rows.length < 2) {
      const idx = this._rows.length + 1;
      this._rows.push({ id: `row${idx}`, title: `Riga ${idx}`, answer_id: null });
    }
    this.displayRows = [...this._rows];
  }
  get rows(): RowDef[] {
    return [...this._rows];
  }

  constructor(private cd: ChangeDetectorRef) {}

  /** Ricombina l'array displayedColumns per mat-table */
  private updateTableColumns(): void {
    this.displayedColumns = ['rowID', ...this.displayColumns.map(c => c.id)];
    this.cd.markForCheck();
  }

  private emitChanges(): void {
    // Emesso sempre string IDs
    this.columnsChange.emit(this.displayColumns.map(c => ({ ...c })));
    this.rowsChange.emit(this.displayRows.map(r => ({ ...r })));
    this._columns = [...this.displayColumns];
    this._rows = [...this.displayRows];
    this.updateTableColumns();
  }

  addColumn(): void {
    const idx = this.displayColumns.length + 1;
    this.displayColumns = [
      ...this.displayColumns,
      { id: `col${idx}`, title: `Colonna ${idx}` }
    ];
    this.emitChanges();
  }

  removeColumn(index: number): void {
    this.displayColumns = this.displayColumns.filter((_, i) => i !== index);
    this.emitChanges();
  }

  addRow(): void {
    const idx = this.displayRows.length + 1;
    this.displayRows = [
      ...this.displayRows,
      { id: `row${idx}`, title: `Riga ${idx}`, answer_id: null }
    ];
    this.emitChanges();
  }

  removeRow(index: number): void {
    this.displayRows = this.displayRows.filter((_, i) => i !== index);
    this.emitChanges();
  }

  /** Callback quando cambia la risposta di una riga */
  onAnswerChange(changedRow: RowDef): void {
    const idx = this.displayRows.findIndex(r => r.id === changedRow.id);
    if (idx > -1) {
      // forziamo string value
      this.displayRows[idx] = { ...changedRow, answer_id: String(changedRow.answer_id) };
      this.rowsChange.emit(this.displayRows.map(r => ({ ...r })));
      this.cd.markForCheck();
    }
  }
}
